import { useEffect, useRef } from 'react';
import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';
import * as GlobalSearch from 'global-search';

/**
 * Which exposed mount function of the Global Search remote to render.
 *  - 'search'  -> mountDistributedGlobalSearch        (the header search bar)
 *  - 'results' -> mountDistributedGlobalSearchResults  (the results page)
 *  - 'history' -> mountDistributedGlobalSearchHistory  (the history page)
 */
export type GlobalSearchVariant = 'search' | 'results' | 'history';

const MOUNTERS: Record<GlobalSearchVariant, keyof typeof import('globalSearch/mount')> = {
  search: 'mountDistributedGlobalSearch',
  results: 'mountDistributedGlobalSearchResults',
  history: 'mountDistributedGlobalSearchHistory',
};

/**
 * The bundled `global-search` npm package components, used as the fallback
 * implementation when the Module Federation remote is turned OFF.
 */
const PACKAGE_COMPONENTS: Record<GlobalSearchVariant, React.ComponentType<any>> = {
  search: GlobalSearch.DistributedGlobalSearch,
  results: GlobalSearch.DistributedGlobalSearchResults,
  history: GlobalSearch.DistributeGlobalSearchHistory,
};

/**
 * Harness flag that switches the Global Search implementation:
 *  - ON  -> load the micro-frontend at runtime via Module Federation (MFE).
 *  - OFF -> use the build-time bundled `global-search` npm package.
 * Defaults to OFF (package) until the flag streams in.
 */
export const GLOBAL_SEARCH_MFE_FLAG = 'UNIFY_UI_GLOBALSEARCH_MFE';

interface RemoteHandle {
  update: (p: Record<string, unknown>) => void;
  unmount: () => void;
}

export interface GlobalSearchRemoteProps {
  variant?: GlobalSearchVariant;
  className?: string;
  style?: import('react').CSSProperties;
  /**
   * Any props forwarded to the Global Search widget (accessToken, query,
   * onChange, storeId, userName, lng, darkMode, unifyUserApplications,
   * harnessFlags, onClickCallback, viewMore, viewSearchHistory, ...).
   * `query` is REQUIRED and controlled by the host.
   */
  [key: string]: unknown;
}

/**
 * Host-side wrapper for Global Search.
 *
 * When the `UNIFY_UI_GLOBALSEARCH_MFE` Harness flag is ON, it loads the Global
 * Search micro-frontend at runtime via Module Federation, mounts the remote
 * widget into a ref'd div, pushes prop updates through the returned handle, and
 * unmounts on cleanup. React is NOT shared across that boundary - the remote
 * renders its own React tree.
 *
 * When the flag is OFF (default), it renders the build-time bundled
 * `global-search` npm package component for the requested variant, preserving
 * the previous behaviour so the two implementations can coexist.
 */
export default function GlobalSearchRemote(props: GlobalSearchRemoteProps) {
  const { variant = 'search', className, style, ...remoteProps } = props;
  const mfeEnabled = useFeatureFlag(GLOBAL_SEARCH_MFE_FLAG) === true;

  const containerRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<RemoteHandle | null>(null);
  const propsRef = useRef(remoteProps);
  propsRef.current = remoteProps;

  useEffect(() => {
    if (!mfeEnabled) return;
    let cancelled = false;
    import('globalSearch/mount')
      .then((remote) => {
        if (cancelled || !containerRef.current) return;
        const mounter = remote[MOUNTERS[variant]];
        handleRef.current = mounter(containerRef.current, propsRef.current) as RemoteHandle;
      })
      .catch((err) => {
        // Do not crash the host if the remote is unavailable.
        // eslint-disable-next-line no-console
        console.error('[GlobalSearchRemote] failed to load globalSearch/mount', err);
      });
    return () => {
      cancelled = true;
      handleRef.current?.unmount();
      handleRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, mfeEnabled]);

  // Push prop updates on every render (the remote owns diffing internally).
  useEffect(() => {
    if (!mfeEnabled) return;
    handleRef.current?.update(propsRef.current);
  });

  if (!mfeEnabled) {
    const PackageComponent = PACKAGE_COMPONENTS[variant];
    return <PackageComponent {...remoteProps} />;
  }

  return <div ref={containerRef} className={className} style={style} />;
}
